import { createReducer } from 'typesafe-actions';
import { get, camelCaseKeys } from '../../../common/utils/objectUtils';
import findLink from '../utils';
import { defaultErrorHandler, defaultRequestHandler } from '../dataFetching/generators';
import mapResponseToProduct from './mapResponseToProduct';
import generateProductPlaceHolders from './productPlaceholder';
import {
    ProductsState,
    CrossCategoryNavigation,
    CrossCategoryNavigationCategoryResponse,
    CrossCategoryNavigationResponse,
    DidYouMeanInterface,
    ReducedDidYouMeanInterface,
    ProductsResponse,
    pathFilterNames,
    ProductResponse,
    GenericProductsState,
    SecondaryProductsState,
} from './types';
import {
    ProductsActionsType,
    fetchProductsActions,
    SecondaryProductsActionsType,
    fetchSecondaryProductsActions,
    NewProductsRandomizedActions,
    newRandomizedProductsActions,
    BestSellerProductsActions,
    bestSellerProductsActions,
    ProductRelativesActionsType,
    fetchProductRelativesActions,
} from './actions';

const normalizeCrossCatCategories = (categories: CrossCategoryNavigationCategoryResponse[]): any[] =>
    (categories || []).map((category: CrossCategoryNavigationCategoryResponse) => ({
        name: category.name,
        isSelected: category.isSelected,
        link: findLink(category.links, 'website.product_listing_page'),
    }));

const getCrossNavigation = (crossCategoryNavigation?: CrossCategoryNavigationResponse): CrossCategoryNavigation => {
    // API sometimes returns an empty object
    if (crossCategoryNavigation && Object.keys(crossCategoryNavigation).length > 0) {
        return {
            categories: normalizeCrossCatCategories(crossCategoryNavigation!.categories),
            parentCategory: {
                isRoot: crossCategoryNavigation!.parentCategory.isRoot,
                link: findLink(crossCategoryNavigation!.parentCategory.links, 'website.product_listing_page'),
            },
        };
    }

    return { categories: [], parentCategory: { link: '', isRoot: true } };
};

const mapDidYouMeanAndRelative = (didYouMean: DidYouMeanInterface): ReducedDidYouMeanInterface => {
    const reduced: ReducedDidYouMeanInterface = { title: '', relLink: '' };
    reduced.title = didYouMean.title;
    reduced.relLink = findLink(didYouMean.links, 'website');
    return reduced;
};

const basicProductsDefaultState = {
    identifier: '',
    products: [],
    isFetching: false,
    didInvalidate: false,
    error: null,
};

export const defaultSecondaryProductsDefaultState = {
    products: {
        allProducts: basicProductsDefaultState,
        newProducts: basicProductsDefaultState,
    },
};

export const defaultProductsState: ProductsState = {
    ...basicProductsDefaultState,
    didYouMean: [],
    filters: [],
    sorting: [],
    totalCount: -1,
    pageTitle: '',
    cmsPageTitle: '',
    crossCategoryNavigation: {
        categories: [],
        parentCategory: { isRoot: true, link: '' },
    },
    links: [],
    pdpUrl: '',
};

const getFilters = (payload: ProductsResponse) => {
    if (payload && payload.data && payload.data.filters) {
        return payload.data.filters.map(filter => {
            if (filter.name === 'facet_category') {
                return { ...filter, name: pathFilterNames.categories };
            }
            if (filter.name === 'facet_brand') {
                return { ...filter, name: pathFilterNames.brands };
            }
            return filter;
        });
    }
    return [];
};

export const primaryProductsReducer = createReducer<ProductsState, ProductsActionsType>(defaultProductsState)
    .handleAction(fetchProductsActions.success, (state, action) => {
        const { data } = action.payload.data;
        const { reducerPayloadOptions } = action.payload;
        const { appliedSorting, appliedFilters } = data!;
        const { crossCategoryNavigation } = data!;
        const formatOptions = { countryCode: '', currency: '' };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
        }
        return {
            ...state,
            isFetching: false,
            isFetchingComplete: true,
            didYouMean: get(action.payload.data, 'data.didYouMean', []).map(mapDidYouMeanAndRelative),
            products: get(action.payload.data, 'data.products', []).map((value: ProductResponse, index: number) =>
                mapResponseToProduct(index, value, appliedSorting, appliedFilters, formatOptions)
            ),
            totalCount: get(action.payload.data, 'data.totalCount', 0),
            filters: getFilters(action.payload.data).map(filter => camelCaseKeys(filter, { deep: false })),
            sorting: get(action.payload.data, 'data.sorting', []),
            didInvalidate: false,
            identifier: action.payload.identifier,
            pageTitle: get(action.payload.data, 'data.meta.title', ''),
            cmsPageTitle: get(action.payload.data, 'data.meta.cms_banner_title', ''),
            links: get(action.payload.data, 'data.links', ''),
            crossCategoryNavigation: getCrossNavigation(crossCategoryNavigation),
            pdpUrl: get(action.payload.data, 'data.meta.pdp_url', ''),
        };
    })
    .handleAction(fetchProductsActions.failure, (state, { payload }) => {
        return {
            ...state,
            isFetching: false,
            isFetchingComplete: true,
            didInvalidate: true,
            products: [],
            error: payload.error,
        };
    })
    .handleAction(fetchProductsActions.request, (state, { payload }) => {
        return {
            ...defaultProductsState,
            isFetching: true,
            isFetchingComplete: undefined,
            totalCount: -1,
            identifier: payload.identifier,
            sorting: state.sorting,
            filters: state.filters,
            products: generateProductPlaceHolders(6),
        };
    });

export const secondaryProductsReducer = createReducer<SecondaryProductsState, SecondaryProductsActionsType>(
    defaultSecondaryProductsDefaultState
)
    .handleAction(fetchSecondaryProductsActions.request, (state, action) => {
        const { identifier } = action.payload;

        return {
            ...state,
            products: {
                ...state.products,
                [identifier]: {
                    ...state.products[identifier],
                    isFetching: true,
                    products: generateProductPlaceHolders(3),
                },
            },
        };
    })
    .handleAction(fetchSecondaryProductsActions.success, (state, action) => {
        const { identifier } = action.payload;
        const { reducerPayloadOptions } = action.payload;
        const formatOptions = { countryCode: '', currency: '' };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
        }
        return {
            ...state,
            products: {
                ...state.products,
                [identifier]: {
                    ...state.products[identifier],
                    isFetching: false,
                    products: get(
                        action.payload.data,
                        'data.products',
                        []
                    ).map((value: ProductResponse, index: number) =>
                        mapResponseToProduct(index, value, undefined, undefined, formatOptions)
                    ),
                },
            },
        };
    })
    .handleAction(fetchSecondaryProductsActions.failure, (state, action) => {
        const { identifier } = action.payload;

        return {
            ...state,
            products: {
                ...state.products,
                [identifier]: {
                    ...state.products[identifier],
                    isFetching: false,
                    didInvalidate: true,
                    error: action.payload,
                    products: [],
                },
            },
        };
    });

export const newProductRandomizedReducer = createReducer<GenericProductsState, NewProductsRandomizedActions>(
    basicProductsDefaultState
)
    .handleAction(newRandomizedProductsActions.actions.success, (state, action) => {
        const { reducerPayloadOptions } = action.payload;
        const formatOptions = { countryCode: '', currency: '' };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
        }

        return {
            ...state,
            isFetching: false,
            products: get(action.payload.data, 'data.products', []).map((value: ProductResponse, index: number) =>
                mapResponseToProduct(index, value, undefined, undefined, formatOptions)
            ),
        };
    })
    .handleAction(newRandomizedProductsActions.actions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(newRandomizedProductsActions.actions.request, (state, action) =>
        defaultRequestHandler(state, action)
    );

export const bestSellerProductReducer = createReducer<GenericProductsState, BestSellerProductsActions>(
    basicProductsDefaultState
)
    .handleAction(bestSellerProductsActions.actions.success, (state, action) => {
        const { reducerPayloadOptions } = action.payload;
        const formatOptions = { countryCode: '', currency: '' };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
        }

        return {
            ...state,
            isFetching: false,
            products: get(action.payload.data, 'data', []).map((value: ProductResponse, index: number) =>
                mapResponseToProduct(index, value, undefined, undefined, formatOptions)
            ),
        };
    })
    .handleAction(bestSellerProductsActions.actions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(bestSellerProductsActions.actions.request, (state, action) => defaultRequestHandler(state, action));

export const productRelativesReducer = createReducer<GenericProductsState, ProductRelativesActionsType>(
    basicProductsDefaultState
)
    .handleAction(fetchProductRelativesActions.request, (state, action) => {
        return {
            ...state,
            isFetching: true,
            isFetchingComplete: undefined,
            identifier: action.payload.identifier,
            didInvalidate: false,
            products: generateProductPlaceHolders(5),
            error: undefined,
        };
    })
    .handleAction(fetchProductRelativesActions.success, (state, action) => {
        const { reducerPayloadOptions } = action.payload;
        const formatOptions = { countryCode: '', currency: '' };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
        }

        return {
            ...state,
            isFetching: false,
            isFetchingComplete: true,
            didInvalidate: false,
            identifier: action.payload.identifier,
            products: get(action.payload.data, 'data', []).map((value: ProductResponse, index: number) =>
                mapResponseToProduct(index, value, undefined, undefined, formatOptions)
            ),
        };
    })
    .handleAction(fetchProductRelativesActions.failure, (state, action) => {
        return {
            ...state,
            isFetching: false,
            isFetchingComplete: true,
            products: [],
            error: action.payload.error,
        };
    });
